import { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Smartphone, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const BeforeAfterSlider = () => {
  const { t } = useLanguage();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  // Before: normal phone camera photo
  const beforeImage = 'https://customer-assets.emergentagent.com/job_aifood-photos/artifacts/mu0e33cq_normal.jpeg';
  // After: AI enhanced by MenuRank
  const afterImage = 'https://customer-assets.emergentagent.com/job_aifood-photos/artifacts/7bx69jz1_ai.png';

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(5, Math.min(95, (x / rect.width) * 100));
    setSliderPosition(percent);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    handleMove(e.touches[0].clientX);
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <section 
      id="comparison" 
      ref={sectionRef}
      className="py-20 md:py-32 relative"
      data-testid="comparison-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FFB703] text-sm font-medium uppercase tracking-wider mb-4 block">
            {t.comparison.label}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-4">
            {t.comparison.title} <span className="text-gradient">{t.comparison.titleHighlight}</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            {t.comparison.subtitle}
          </p>
        </motion.div>

        {/* Slider Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div
            ref={containerRef}
            className="relative aspect-[4/3] rounded-3xl overflow-hidden cursor-ew-resize select-none glow-orange"
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
            data-testid="comparison-slider"
          >
            {/* After Image (Bottom Layer) */}
            <div className="absolute inset-0">
              <img
                src={afterImage}
                alt="AI Enhanced"
                className="w-full h-full object-cover"
                draggable={false}
              />
              {/* After Label */}
              <div className="absolute top-4 right-4 bg-gradient-to-r from-[#F59E0B] to-[#FFB703] px-4 py-2 rounded-full flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#0d0a07]" />
                <span className="text-sm font-semibold text-[#0d0a07]">{t.comparison.after}</span>
              </div>
            </div>

            {/* Before Image (Top Layer - Clipped) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src={beforeImage}
                alt="Phone Camera"
                className="w-full h-full object-cover grayscale-[30%] brightness-90"
                draggable={false}
              />
              {/* Before Label */}
              <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-white/70" />
                <span className="text-sm font-medium text-white/70">{t.comparison.before}</span>
              </div>
            </div>

            {/* Slider Handle */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
              {/* Handle Grip */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                <div className="flex gap-1">
                  <div className="w-0.5 h-4 bg-gray-400 rounded-full" />
                  <div className="w-0.5 h-4 bg-gray-400 rounded-full" />
                </div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <p className="text-center text-white/40 text-sm mt-4">
            {t.comparison.instruction}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
